import type { Money } from "@ai-platform/contracts";

/*
 * Numbers and dates for display, in the locale of the platform (de-AT, as the database's
 * collation, 06). Money arrives as micro units in a string (K-06, D-03) and never becomes a
 * float: the micros turn into an exact decimal string, which `Intl.NumberFormat` formats as it
 * is (07 §5: amounts from micros, dollars with two decimals).
 */

const LOCALE = "de-AT";

const MICRO_DIGITS = 6;

/** `"1234567"` → `"1.234567"`, `"-5"` → `"-0.000005"`: the amount in whole units, exact. */
export function microsToDecimal(micros: string): `${number}` {
  const negative = micros.startsWith("-");
  const digits = (negative ? micros.slice(1) : micros).padStart(MICRO_DIGITS + 1, "0");
  const whole = digits.slice(0, -MICRO_DIGITS);
  const fraction = digits.slice(-MICRO_DIGITS);
  return `${negative ? "-" : ""}${whole}.${fraction}` as `${number}`;
}

const moneyFormats = new Map<string, Intl.NumberFormat>();

function moneyFormat(currency: string): Intl.NumberFormat {
  let format = moneyFormats.get(currency);
  if (!format) {
    format = new Intl.NumberFormat(LOCALE, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    moneyFormats.set(currency, format);
  }
  return format;
}

/** An amount with its currency and two decimals, e.g. `$ 400,00`. */
export function formatMoney(money: Money): string {
  return moneyFormat(money.currency).format(microsToDecimal(money.micros));
}

/**
 * Whether an amount is below zero, read from the sign of its micros as the API sends them, e.g. an
 * available amount after a lowered limit (ADR-149), which stands in the error colour.
 */
export function isNegative(money: Money): boolean {
  return money.micros.startsWith("-");
}

/** Whole units and at most two decimals, with a comma or a point; no thousands separators. */
const AMOUNT_INPUT = /^(\d{1,12})(?:[.,](\d{1,2}))?$/u;

/**
 * An amount as people type it, `"250"`, `"250,5"` or `"250.50"`, as micro units in a string
 * (K-06), exact and without a float; `null` for anything else. No thousands separators, so
 * `"1.250"` is not taken for 1250 or for 1.25.
 */
export function amountToMicros(input: string): string | null {
  const match = AMOUNT_INPUT.exec(input.trim());
  if (!match) return null;
  const [, whole = "0", decimals = ""] = match;
  return (BigInt(whole) * 10n ** BigInt(MICRO_DIGITS) + BigInt(decimals.padEnd(MICRO_DIGITS, "0"))).toString();
}

/**
 * Micro units as the amount fields take them, `"250000000"` → `"250,00"`, exact; `""` when the
 * micros carry more than two decimals, which a field could not give back unchanged.
 */
export function microsToAmountInput(micros: string): string {
  const [whole = "0", fraction = ""] = microsToDecimal(micros).split(".");
  if (/[1-9]/u.test(fraction.slice(2))) return "";
  return `${whole},${fraction.slice(0, 2)}`;
}

const dateFormat = new Intl.DateTimeFormat(LOCALE, { day: "2-digit", month: "2-digit", year: "numeric" });
const dateTimeFormat = new Intl.DateTimeFormat(LOCALE, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  // midnight is 00:00 of the new day; some engines write 24:00 of the old one without it
  hourCycle: "h23",
});
const dayFormat = new Intl.DateTimeFormat(LOCALE, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "UTC",
});
const countFormat = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });

/** The calendar day of an ISO timestamp in the browser's time zone, e.g. `18.03.2026`. */
export function formatDate(timestamp: string): string {
  return dateFormat.format(new Date(timestamp));
}

/** Day and time of an ISO timestamp in the browser's time zone, e.g. `14.10.2026, 08:00`. */
export function formatDateTime(timestamp: string): string {
  return dateTimeFormat.format(new Date(timestamp));
}

/**
 * A day without a time, `YYYY-MM-DD`, e.g. the day of a usage item (UTC, 06 `usage_item`), as
 * `04.10.2026`. Read in UTC, so it does not slip to the day before west of Greenwich.
 */
export function formatDay(day: string): string {
  return dayFormat.format(new Date(`${day}T00:00:00Z`));
}

/**
 * A count, requests or tokens, with the thousands grouped as de-AT groups numbers that are not
 * money: by a no-break space, e.g. `24 400 000` (amounts keep the point, `$ 1.234,00`).
 */
export function formatCount(count: number): string {
  return countFormat.format(count);
}
